import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  investorFormTemplateService,
  openPdfBlob,
  TemplateSummary,
} from '../../services/formTemplate.service';
import { getSAProxyInvestorId } from '../../services/saProxyAdapter';

/**
 * EXTRA forms (template type OTHER) the investor's service provider configured through the
 * admin SP Form Templates screen, pre-filled with the investor's data. Booklet / CKYC / FATCA
 * templates are not listed here — the requirement rows' own Download links already produce
 * them. Renders nothing when the provider has no extra forms.
 */
export const SpFormsPanel: React.FC<{ journeyId?: string; disabled?: boolean; disabledReason?: string; investorName?: string }> = ({
  journeyId, disabled, disabledReason, investorName,
}) => {
  const [forms, setForms] = useState<TemplateSummary[]>([]);
  const [downloading, setDownloading] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    // Service agents view this page through the SA proxy; the investor-only endpoint has no
    // proxy counterpart, so stay hidden for them rather than call it with the agent's token.
    if (getSAProxyInvestorId() != null) return undefined;
    investorFormTemplateService.list(journeyId)
      // BOOKLET / CKYC / FATCA templates are already served by the requirement rows above
      // (the Account Opening Booklet / CKYC / FATCA "Download" links pick them up); listing
      // them again here would show the same form twice. Only extra forms belong in this panel.
      .then((list) => { if (!cancelled) setForms(list.filter((f) => f.templateType === 'OTHER')); })
      .catch(() => { if (!cancelled) setForms([]); });
    return () => { cancelled = true; };
  }, [journeyId]);

  if (forms.length === 0) return null;

  const download = async (form: TemplateSummary) => {
    if (disabled) {
      toast.warn(disabledReason || 'Not available yet');
      return;
    }
    try {
      setDownloading(form.id);
      toast.info(`Generating ${form.name}, please wait…`);
      const blob = await investorFormTemplateService.download(form.id, journeyId);
      if (blob.size === 0) throw new Error('Generated PDF is empty');
      const clean = (v: string) => v.trim().replace(/[^A-Za-z0-9_-]+/g, '_').replace(/_+/g, '_').replace(/^_+|_+$/g, '');
      const person = clean(investorName ?? '');
      const base = clean(form.name) || `form-${form.id}`;
      openPdfBlob(blob, `${person ? `${base}_${person}` : base}.pdf`);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Could not generate the form');
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="mb-3 p-2.5 bg-slate-50 rounded-lg border border-slate-200">
      <div className="flex items-center gap-1.5 mb-1.5 text-[#3e6f7c] font-bold text-[11px]">
        <i className="bi bi-file-earmark-pdf-fill" />
        <span>Forms from your service provider</span>
      </div>
      <p className="m-0 mb-2 text-[11px] text-slate-500">
        Pre-filled with the details you have provided. Download, sign where indicated and upload with your other documents.
      </p>
      <ul className="list-none p-0 m-0">
        {forms.map((f) => (
          <li key={f.id} className="flex justify-between items-center gap-3 py-1.5 border-t border-slate-200 text-[12px]">
            <span className="font-semibold text-slate-700">
              {f.name}
              {f.pageCount ? <span className="font-normal text-slate-400"> · {f.pageCount} pages</span> : null}
            </span>
            <button
              type="button"
              className="inline-flex items-center bg-[#3e6f7c] hover:bg-[#355f69] text-white text-[11px] font-bold h-[28px] px-3 rounded-md border-0 disabled:opacity-50"
              onClick={() => download(f)}
              disabled={downloading === f.id}
            >
              <i className="bi bi-download me-1.5" />
              {downloading === f.id ? 'Generating…' : 'Download'}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};
